import { useCallback, useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import { sseData, streamEvents } from '../../api/sse'
import { getAccessToken } from '../../auth/tokenStore'
import { describeApiFailure } from '../../lib/failure'
import { assistantKeys } from './queries'
import type {
  AssistantDoneEvent,
  AssistantErrorEvent,
  AssistantReply,
  AssistantSession,
  AssistantTokenEvent,
  AssistantToolEvent,
  ChatMessage,
  PendingReply,
} from './types'

export interface AssistantChat {
  /** The reply arriving right now, token by token. Null when nothing is in flight. */
  pending: PendingReply | null
  isStreaming: boolean
  failure: string | null
  /** True when the last answer came from the non-streaming endpoint after a stream failed. */
  fellBack: boolean
  ask: (message: string) => void
  stop: () => void
  dismissFailure: () => void
}

/**
 * Asks the assistant, streaming the answer.
 *
 * **`EventSource` cannot be used and the reason is the endpoint's shape.** The prompt is
 * the request *body* — prompts do not belong in a URL — so the stream is a `POST`, and
 * `EventSource` only issues `GET`s and cannot set an `Authorization` header. The body is
 * read by hand through `api/sse.ts`, which is why the bearer is passed explicitly here.
 *
 * **The fallback is a different endpoint, not a retry.** `POST assistant/messages` answers
 * the same question without streaming, and the API's own note is that *the two persist
 * identically* — so falling back costs the token-by-token effect and nothing else. A
 * stream that dies mid-answer, though, is **not** retried: the API deliberately persists
 * nothing for a half-streamed assistant turn, so a retry would ask the model twice and
 * store two answers to one question. Only a stream that fails before its first token falls
 * back.
 *
 * **The attached character is not passed here.** It is a property of the session on the
 * server, so the question carries only the question — which is what keeps a mid-stream
 * context change from being a question asked about one character and answered about another.
 *
 * The transcript itself is server state and stays in the query cache. This hook owns only
 * the in-flight message, and on `done` writes both persisted turns into that cache — no
 * refetch, for the same reason no sheet is refetched after a mutation.
 */
export function useAssistantChat(): AssistantChat {
  const queryClient = useQueryClient()

  const [pending, setPending] = useState<PendingReply | null>(null)
  const [isStreaming, setIsStreaming] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)
  const [fellBack, setFellBack] = useState(false)

  const abort = useRef<AbortController | null>(null)

  /** Appends both persisted turns to the cached transcript. */
  const appendTurns = useCallback(
    (userMessage: ChatMessage, reply: ChatMessage) => {
      queryClient.setQueryData<AssistantSession>(assistantKeys.session(), (previous) =>
        previous === undefined
          ? previous
          : { ...previous, messages: [...(previous.messages ?? []), userMessage, reply] },
      )
    },
    [queryClient],
  )

  const ask = useCallback(
    (message: string) => {
      const question = message.trim()
      if (question === '') return

      abort.current?.abort()
      const controller = new AbortController()
      abort.current = controller

      setPending({ question, text: '', tool: null })
      setIsStreaming(true)
      setFailure(null)
      setFellBack(false)

      void (async () => {
        let sawToken = false

        try {
          for await (const event of streamEvents('/assistant/messages/stream', {
            method: 'POST',
            body: { message: question },
            accessToken: getAccessToken(),
            signal: controller.signal,
          })) {
            if (event.event === 'token') {
              const token = sseData<AssistantTokenEvent>(event)
              if (token !== null) {
                sawToken = true
                setPending((current) =>
                  current === null
                    ? current
                    : // The answer has started, so whatever tool was running is finished.
                      { ...current, text: current.text + token.text, tool: null },
                )
              }
              continue
            }

            if (event.event === 'tool') {
              const tool = sseData<AssistantToolEvent>(event)
              // A status line, not a log: only the latest tool is kept. Somebody waiting wants to
              // know something is happening, not to read a transcript of the machinery.
              if (tool !== null) {
                setPending((current) =>
                  current === null ? current : { ...current, tool: tool.tool },
                )
              }
              continue
            }

            if (event.event === 'done') {
              const done = sseData<AssistantDoneEvent>(event)
              if (done !== null) appendTurns(done.userMessage, done.reply)
              setPending(null)
              setIsStreaming(false)
              return
            }

            if (event.event === 'error') {
              const failed = sseData<AssistantErrorEvent>(event)
              // A server-sent `error` is the model failing, not the transport — the API
              // has already decided not to persist an assistant turn, and asking again
              // would be the user's call, not ours.
              setFailure(failed?.message ?? 'The assistant could not answer.')
              setPending(null)
              setIsStreaming(false)
              return
            }
          }

          // The stream ended without `done` or `error`. If tokens had already arrived the
          // answer exists but was never persisted; saying so is better than silently
          // re-asking and storing a second answer.
          if (sawToken) {
            setFailure('The answer was cut off before it was saved. Ask again if you need it.')
            setPending(null)
            setIsStreaming(false)
            return
          }

          await fallback(question, controller.signal)
        } catch (error) {
          if (controller.signal.aborted) return

          if (sawToken) {
            setFailure('The answer was cut off before it was saved. Ask again if you need it.')
            setPending(null)
            setIsStreaming(false)
            return
          }

          // Nothing streamed, so nothing was persisted for the assistant turn and asking
          // the non-streaming endpoint costs one answer, not two.
          await fallback(question, controller.signal, error)
        }
      })()

      async function fallback(question: string, signal: AbortSignal, cause?: unknown) {
        try {
          const reply = await api.post<AssistantReply>(
            '/assistant/messages',
            { message: question },
            { signal },
          )

          if (reply.userMessage !== undefined && reply.reply !== undefined) {
            appendTurns(reply.userMessage, reply.reply)
          }
          setFellBack(true)
        } catch (error) {
          if (signal.aborted) return
          setFailure(describeApiFailure(cause ?? error, 'You are not signed in.'))
        } finally {
          setPending(null)
          setIsStreaming(false)
        }
      }
    },
    [appendTurns],
  )

  const stop = useCallback(() => {
    abort.current?.abort()
    setPending(null)
    setIsStreaming(false)
  }, [])

  const dismissFailure = useCallback(() => setFailure(null), [])

  useEffect(() => () => abort.current?.abort(), [])

  return { pending, isStreaming, failure, fellBack, ask, stop, dismissFailure }
}
